/**
 * Originally published as:
 * https://github.com/volverjs/zod-vue-i18n
 *
 * Forked by Alex Riviere for modifications
 *
 * @license
 * MIT License
 *
 * Copyright (c) 2023 Volver
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 *
 */

import {
  type ZodIssueOptionalMessage,
  type ErrorMapCtx,
  type ZodErrorMap,
  ZodIssueCode,
  ZodParsedType,
  defaultErrorMap,
  z,
} from "../zod";
import type { I18n, NamedValue } from "vue-i18n";
import type { I18nTranslate, I18nDateFormatter, I18nTranslateOptions } from "../types/common";

declare module "../zod" {
  export interface ZodCustomIssue {
    i18n?: { key: string; options?: I18nTranslateOptions } | string;
  }
}

export const jsonStringifyReplacer = (_: string, value: unknown): unknown => {
  if (typeof value === "bigint") {
    return value.toString();
  }
  return value;
};

export function joinValues<T extends unknown[]>(array: T, separator = " | "): string {
  return array.map((val) => (typeof val === "string" ? `'${val}'` : val)).join(separator);
}

const zDate = z.string().regex(/(\d{4})-\d{2}-(\d{2})/);

const makeZodI18nMap =
  (i18n: I18n, key = "errors"): ZodErrorMap =>
  (issue: ZodIssueOptionalMessage, ctx: ErrorMapCtx): { message: string } => {
    let message: string;
    message = defaultErrorMap(issue, ctx).message;

    let options = {} as I18nTranslateOptions;

    const t = i18n.global.t as I18nTranslate;
    const te = i18n.global.te;
    const d = i18n.global.d as I18nDateFormatter;

    const translateLabel = (message: string, options: NamedValue) => {
      if (te(`${key}.${message}WithPath`)) {
        return t(`${key}.${message}WithPath`, options);
      }
      if (te(`${key}.${message}`)) {
        return t(`${key}.${message}`, options);
      }
      if (te(message)) {
        return t(message, options);
      }
      return message;
    };
    if (import.meta.env.VITE_DEBUG_ZOD) {
      devConsole.warn("zodForm Validation Error", message, issue, ctx);
    }

    switch (issue.code) {
      case ZodIssueCode.invalid_type:
        if (issue.received === ZodParsedType.undefined) {
          message = "invalidTypeReceivedUndefined";
        } else {
          message = "invalidType";
          options = {
            expected: te(`types.${issue.expected}`) ? t(`types.${issue.expected}`) : issue.expected,
            received: te(`types.${issue.received}`) ? t(`types.${issue.received}`) : issue.received,
          };
        }
        break;
      case ZodIssueCode.invalid_literal:
        message = "invalidLiteral";
        options = {
          expected: JSON.stringify(issue.expected, jsonStringifyReplacer),
        };
        break;
      case ZodIssueCode.unrecognized_keys:
        message = "unrecognizedKeys";
        options = {
          keys: joinValues(issue.keys, ", "),
        };
        break;
      case ZodIssueCode.invalid_union:
        message = "invalidUnion";
        break;
      case ZodIssueCode.invalid_union_discriminator:
        message = "invalidUnionDiscriminator";
        options = {
          options: joinValues(issue.options),
        };
        break;
      case ZodIssueCode.invalid_enum_value:
        message = "invalidEnumValue";
        options = {
          options: joinValues(issue.options),
          received: issue.received,
        };
        break;
      case ZodIssueCode.invalid_arguments:
        message = "invalidArguments";
        break;
      case ZodIssueCode.invalid_return_type:
        message = "invalidReturnType";
        break;
      case ZodIssueCode.invalid_date:
        message = "invalidDate";
        break;
      case ZodIssueCode.invalid_string:
        if (typeof issue.validation === "object") {
          if ("startsWith" in issue.validation) {
            message = `invalidString.startsWith`;
            options = {
              startsWith: issue.validation.startsWith,
            };
          } else if ("endsWith" in issue.validation) {
            message = `invalidString.endsWith`;
            options = {
              endsWith: issue.validation.endsWith,
            };
          }
        } else {
          message = `invalidString.${issue.validation}`;
          options = {
            validation: t(`validations.${issue.validation}`, ctx),
          };
        }
        break;
      case ZodIssueCode.too_small:
        message = `tooSmall.${issue.type}.${issue.exact ? "exact" : issue.inclusive ? "inclusive" : "notInclusive"}`;
        options = {
          minimum: issue.type === "date" ? d(new Date(issue.minimum as string | number)) : issue.minimum,
        };

        break;
      case ZodIssueCode.too_big:
        message = `tooBig.${issue.type}.${issue.exact ? "exact" : issue.inclusive ? "inclusive" : "notInclusive"}`;
        options = {
          maximum: issue.type === "date" ? d(new Date(issue.maximum as string | number)) : issue.maximum,
        };
        break;
      case ZodIssueCode.custom: {
        message = "custom";
        // TODO (JM): ckbase works with params, ckclient works with i18n, we need to unify this
        const i18n = issue.i18n ?? issue.params?.i18n;
        if (i18n) {
          if (typeof i18n === "string") {
            message = i18n;
            break;
          }
          if (typeof i18n === "object" && i18n.key) {
            message = i18n.key;
            if (i18n.options) {
              options = i18n.options;
            }
          }
        }

        break;
      }
      case ZodIssueCode.invalid_intersection_types:
        message = "invalidIntersectionTypes";
        break;
      case ZodIssueCode.not_multiple_of:
        message = "notMultipleOf";
        options = {
          multipleOf: issue.multipleOf,
        };
        break;
      case ZodIssueCode.not_finite:
        message = "notFinite";
        break;
    }
    const formattedOptions = options as NamedValue;
    formattedOptions.path = issue.path.join(".") || "";
    message = translateLabel(message, formattedOptions);

    return { message };
  };

export { zDate, makeZodI18nMap };
